import type { QueryClient } from "@tanstack/react-query";
import { notFound, redirect } from "@tanstack/react-router";
import {
  hasOrgPermission,
  hasProjectPermission,
} from "@/app/capabilities/api/check-permission";
import {
  orgCapabilitiesQueryOptions,
  projectCapabilitiesQueryOptions,
} from "@/app/capabilities/api/get-capabilities";
import type {
  OrgPermission,
  ProjectCapabilities,
  ProjectPermission,
} from "@/app/capabilities/types";

/**
 * Route guards: imperative assertions called inside a route's `beforeLoad`,
 * forwarding the loader context. They live in the routing layer (the `/403`
 * redirect and `notFound()` are routing concerns); the permission *check* stays
 * in the capabilities domain (`hasProjectPermission`).
 *
 * A denied permission is forbidden access to something that exists, so it throws a
 * `/403` redirect. A disabled capability means the feature does not exist for the
 * org, so it throws `notFound()` (404); the route must declare a `notFoundComponent`.
 *
 *   beforeLoad: (ctx) => requireProjectPermission(ctx, PROJECT.secrets.read)
 */

type ProjectGuardArgs = {
  context: { queryClient: QueryClient };
  params: { organizationId: string; projectId: string };
};

/** Project capabilities exposed as a simple on/off feature flag (not a permission set). */
type ProjectCapabilityFlag = Exclude<keyof ProjectCapabilities, "authz">;

const scopeOf = (params: ProjectGuardArgs["params"]) => ({
  organizationId: params.organizationId,
  projectId: params.projectId,
});

export async function requireProjectPermission(
  { context: { queryClient }, params }: ProjectGuardArgs,
  permission: ProjectPermission
): Promise<void> {
  const capabilities = await queryClient.ensureQueryData(
    projectCapabilitiesQueryOptions(scopeOf(params))
  );

  if (!hasProjectPermission(capabilities.authz, permission)) {
    throw redirect({
      to: "/403",
      search: {
        requiredPermission: permission,
        organizationId: params.organizationId,
        projectId: params.projectId,
      },
    });
  }
}

/**
 * Allow the route when the caller holds one or more of the listed `permissions`
 * (not every project permission — just one of these). Use for a page composed of
 * independently-gated sections that is worth showing as long as at least one is
 * visible; each section still gates its own content. Denied access throws a
 * generic `/403`, since no single permission is "the" one required.
 */
export async function requireOneOfProjectPermission(
  { context: { queryClient }, params }: ProjectGuardArgs,
  permissions: ProjectPermission[]
): Promise<void> {
  const capabilities = await queryClient.ensureQueryData(
    projectCapabilitiesQueryOptions(scopeOf(params))
  );

  const allowed = permissions.some((permission) =>
    hasProjectPermission(capabilities.authz, permission)
  );

  if (!allowed) {
    throw redirect({
      to: "/403",
      search: {
        organizationId: params.organizationId,
        projectId: params.projectId,
      },
    });
  }
}

export async function requireProjectCapability(
  { context: { queryClient }, params }: ProjectGuardArgs,
  capability: ProjectCapabilityFlag
): Promise<void> {
  const capabilities = await queryClient.ensureQueryData(
    projectCapabilitiesQueryOptions(scopeOf(params))
  );

  if (!capabilities[capability]?.enabled) {
    throw notFound();
  }
}

type OrgGuardArgs = {
  context: { queryClient: QueryClient };
  // `projectId` is present when the org-scoped guard runs on a project route
  // (e.g. billing) and absent on a truly org-only route; forwarding it keeps the
  // /403 "Go Home" link in the project the user was in.
  params: { organizationId: string; projectId?: string };
};

/**
 * Org-level counterpart of {@link requireOneOfProjectPermission}: allow an org
 * route when the caller holds at least one of the listed org permissions (e.g.
 * organization metadata and organization members). Org RBAC surfaces only exist
 * when operator-RBAC is enabled — their endpoints 404 otherwise — so a disabled
 * org is "feature absent" (`notFound()`), not allow-all. With RBAC on, holding
 * none of the permissions is a generic `/403`. The org capabilities endpoint is
 * Org-Admin-only and fails closed, so a non-admin resolves an empty set and is
 * denied.
 */
export async function requireOneOfOrgPermission(
  { context: { queryClient }, params }: OrgGuardArgs,
  permissions: OrgPermission[]
): Promise<void> {
  const capabilities = await queryClient.ensureQueryData(
    orgCapabilitiesQueryOptions(params.organizationId)
  );

  if (!capabilities.authz.enabled) {
    throw notFound();
  }

  const allowed = permissions.some((permission) =>
    hasOrgPermission(capabilities.authz, permission)
  );
  if (!allowed) {
    throw redirect({
      to: "/403",
      search: {
        organizationId: params.organizationId,
        projectId: params.projectId,
      },
    });
  }
}

/**
 * Billing permission guard. Billing is an org-scoped permission, but unlike the
 * org-only surfaces guarded by {@link requireOneOfOrgPermission}, billing predates RBAC
 * and must stay reachable while RBAC is off — so this allows access when RBAC is
 * disabled (via `hasOrgPermission`'s allow-all) and never throws `notFound()`.
 *
 * Whether billing exists for the org is the *capability*, a separate axis: pair
 * this with `requireProjectCapability(ctx, "billing")`, which 404s when billing
 * does not exist. This guard then 403s when billing exists but the caller lacks
 * the permission (`billing_read` to view, `billing_update` to upgrade).
 */
export async function requireBillingPermission(
  { context: { queryClient }, params }: OrgGuardArgs,
  permission: OrgPermission
): Promise<void> {
  const capabilities = await queryClient.ensureQueryData(
    orgCapabilitiesQueryOptions(params.organizationId)
  );

  if (!hasOrgPermission(capabilities.authz, permission)) {
    throw redirect({
      to: "/403",
      search: {
        requiredPermission: permission,
        organizationId: params.organizationId,
        projectId: params.projectId,
      },
    });
  }
}
